import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { nip19 } from 'nostr-tools';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { useReceivedBadgeAwards } from '@/hooks/useBadgeAwards';
import { useProfileBadges } from '@/hooks/useProfileBadges';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, CheckCircle2, Trophy, ShieldCheck, ShieldX } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BadgeCardProps {
  badgeRef: string;
  awardEventId: string;
  checked: boolean;
  onToggle: () => void;
  alreadyAccepted: boolean;
}

function useBadgeDefinitionFromRef(badgeRef: string) {
  const { nostr } = useNostr();
  // badgeRef = "30009:<pubkey>:<d>"
  const parts = badgeRef.split(':');
  const pubkey = parts[1];
  const d = parts[2];

  return useQuery<NostrEvent | null>({
    queryKey: ['nostr', 'badge-def-ref', badgeRef],
    queryFn: async (c) => {
      if (!pubkey || !d) return null;
      const [event] = await nostr.query(
        [{ kinds: [30009], authors: [pubkey], '#d': [d], limit: 1 }],
        { signal: c.signal },
      );
      return event ?? null;
    },
    staleTime: 5 * 60_000,
  });
}

function BadgeCard({ badgeRef, awardEventId, checked, onToggle, alreadyAccepted }: BadgeCardProps) {
  const { data: defEvent, isLoading } = useBadgeDefinitionFromRef(badgeRef);

  const parts = badgeRef.split(':');
  const issuerPubkey = parts[1];
  const d = parts[2];

  const name = defEvent?.tags.find(([t]) => t === 'name')?.[1] ?? d;
  const description = defEvent?.tags.find(([t]) => t === 'description')?.[1];
  const image = defEvent?.tags.find(([t]) => t === 'thumb')?.[1]
    ?? defEvent?.tags.find(([t]) => t === 'image')?.[1];

  let issuerDisplay: string;
  try {
    issuerDisplay = nip19.npubEncode(issuerPubkey).slice(0, 12) + '…';
  } catch {
    issuerDisplay = issuerPubkey?.slice(0, 12) + '…';
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-border/40 bg-muted/20 p-3">
        <Skeleton className="h-12 w-12 rounded-lg shrink-0" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-3 w-40" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-lg border p-3 transition-colors cursor-pointer select-none',
        alreadyAccepted
          ? 'border-green-700/40 bg-green-950/20'
          : checked
          ? 'border-primary/50 bg-primary/5'
          : 'border-border/40 bg-card/60 hover:bg-muted/30',
      )}
      onClick={alreadyAccepted ? undefined : onToggle}
    >
      {image ? (
        <img
          src={image}
          alt={name}
          className="h-12 w-12 rounded-lg object-cover border border-border/40 shrink-0"
        />
      ) : (
        <div className="h-12 w-12 rounded-lg bg-primary/10 border border-border/40 flex items-center justify-center shrink-0">
          <Trophy className="h-6 w-6 text-primary" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{name}</p>
        {description && (
          <p className="text-xs text-muted-foreground line-clamp-1">{description}</p>
        )}
        <p className="text-xs text-muted-foreground mt-0.5">
          Issued by <span className="font-mono">{issuerDisplay}</span>
        </p>
        <p className="text-xs text-muted-foreground font-mono truncate">
          Award: {awardEventId.slice(0, 12)}…
        </p>
      </div>
      {alreadyAccepted ? (
        <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" aria-label="Already accepted" />
      ) : (
        <Checkbox
          checked={checked}
          onCheckedChange={onToggle}
          aria-label={`Select ${name}`}
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </div>
  );
}

export function AcceptBadges() {
  const { user } = useCurrentUser();
  const { mutateAsync: publish, isPending } = useNostrPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: received = [], isLoading: loadingAwards } = useReceivedBadgeAwards(user?.pubkey);
  const { data: profileBadges, isLoading: loadingProfile } = useProfileBadges(user?.pubkey);

  const [selected, setSelected] = useState<Set<string>>(new Set());

  const acceptedIds = new Set(profileBadges?.accepted.map((b) => b.awardEventId) ?? []);

  // Flatten all individual award entries (one per awardee event, not per awardee)
  const allAwards: { badgeRef: string; awardEventId: string }[] = received.map((a) => ({
    badgeRef: a.badgeRef,
    awardEventId: a.event.id,
  }));

  const pending = allAwards.filter((a) => !acceptedIds.has(a.awardEventId));

  const toggleSelect = (awardEventId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(awardEventId)) {
        next.delete(awardEventId);
      } else {
        next.add(awardEventId);
      }
      return next;
    });
  };

  const handleAccept = async () => {
    if (!user || selected.size === 0) return;

    // Build new profile badges event: keep existing + add selected
    const existing = profileBadges?.accepted ?? [];

    // pairs of [a, e] for each badge
    const existingTags: string[][] = existing.flatMap((b) => [
      ['a', b.badgeRef],
      ['e', b.awardEventId],
    ]);

    const newTags: string[][] = allAwards
      .filter((a) => selected.has(a.awardEventId))
      .flatMap((a) => [
        ['a', a.badgeRef],
        ['e', a.awardEventId],
      ]);

    const tags = [...existingTags, ...newTags];

    try {
      await publish({ kind: 10008, content: '', tags });

      toast({
        title: 'Badges accepted!',
        description: `${selected.size} badge(s) added to your profile.`,
      });
      setSelected(new Set());
      await queryClient.invalidateQueries({ queryKey: ['nostr', 'profile-badges', user.pubkey] });
    } catch (err) {
      toast({
        title: 'Failed to accept badges',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const handleRevoke = async (awardEventId: string) => {
    if (!user) return;

    const updated = (profileBadges?.accepted ?? []).filter((b) => b.awardEventId !== awardEventId);
    const tags: string[][] = updated.flatMap((b) => [
      ['a', b.badgeRef],
      ['e', b.awardEventId],
    ]);

    try {
      await publish({ kind: 10008, content: '', tags });
      toast({ title: 'Badge removed from profile.' });
      await queryClient.invalidateQueries({ queryKey: ['nostr', 'profile-badges', user.pubkey] });
    } catch (err) {
      toast({
        title: 'Failed to update profile badges',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  if (!user) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-10 text-center">
          <p className="text-muted-foreground">Please log in to manage your received badges.</p>
        </CardContent>
      </Card>
    );
  }

  const isLoading = loadingAwards || loadingProfile;

  return (
    <div className="space-y-4">
      {/* Pending awards */}
      <Card className="border-border/60 bg-card/80 backdrop-blur">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Pending Awards</CardTitle>
          </div>
          <CardDescription>
            Select badges to accept and display on your Nostr profile.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? (
            [...Array(2)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 rounded-lg border border-border/40 p-3">
                <Skeleton className="h-12 w-12 rounded-lg" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
              </div>
            ))
          ) : pending.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border py-8 text-center">
              <Trophy className="mx-auto h-8 w-8 text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">No pending badge awards.</p>
            </div>
          ) : (
            pending.map((award) => (
              <BadgeCard
                key={award.awardEventId}
                badgeRef={award.badgeRef}
                awardEventId={award.awardEventId}
                checked={selected.has(award.awardEventId)}
                onToggle={() => toggleSelect(award.awardEventId)}
                alreadyAccepted={false}
              />
            ))
          )}

          {pending.length > 0 && (
            <Button
              className="w-full mt-2"
              onClick={handleAccept}
              disabled={isPending || selected.size === 0}
            >
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isPending
                ? 'Accepting…'
                : selected.size > 0
                ? `Accept ${selected.size} selected badge(s)`
                : 'Select badges to accept'}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Accepted badges on profile */}
      {(profileBadges?.accepted.length ?? 0) > 0 && (
        <Card className="border-border/60 bg-card/80 backdrop-blur">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <ShieldX className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-lg">On Your Profile</CardTitle>
            </div>
            <CardDescription>
              These badges are currently displayed on your Nostr profile (kind 10008). Click to remove.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {profileBadges!.accepted.map((b) => (
              <div key={b.awardEventId} className="relative">
                <BadgeCard
                  badgeRef={b.badgeRef}
                  awardEventId={b.awardEventId}
                  checked={false}
                  onToggle={() => {}}
                  alreadyAccepted={true}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  className="absolute top-2 right-2 h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => handleRevoke(b.awardEventId)}
                  disabled={isPending}
                >
                  Remove
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
